import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useTestimonials, useDeleteTestimonial, useUpdateTestimonial } from '@/hooks/useTestimonials';
import { useTranslation } from '@/features/i18n';
import type { Testimonial } from '@/types/database';

export function TestimonialsAdminPage() {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<Testimonial | null>(null);

  const { data: testimonials, isLoading } = useTestimonials({ includeUnpublished: true });
  const deleteTestimonial = useDeleteTestimonial();
  const updateTestimonial = useUpdateTestimonial();
  const { t } = useTranslation();

  const handleDelete = async () => {
    if (testimonialToDelete) {
      await deleteTestimonial.mutateAsync(testimonialToDelete.id);
      setDeleteModalOpen(false);
      setTestimonialToDelete(null);
    }
  };

  const handleTogglePublish = async (item: Testimonial) => {
    await updateTestimonial.mutateAsync({
      id: item.id,
      status: item.status === 'published' ? 'draft' : 'published',
    });
  };

  const handleToggleFeatured = async (item: Testimonial) => {
    await updateTestimonial.mutateAsync({
      id: item.id,
      is_featured: !item.is_featured,
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.testimonials.title} noIndex />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.testimonials.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.testimonials.subtitle}
            </p>
          </div>
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            {t.admin.testimonials.newTestimonial}
          </Button>
        </div>

        {testimonials && testimonials.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="h-full p-3 sm:p-4 lg:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {testimonial.avatar_url ? (
                        <img
                          src={testimonial.avatar_url}
                          alt={testimonial.name}
                          className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 dark:text-primary-400 font-medium text-sm sm:text-base">
                            {testimonial.name[0]}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-medium text-neutral-900 dark:text-white text-sm sm:text-base truncate">
                          {testimonial.name}
                        </h3>
                        <p className="text-xs sm:text-sm text-neutral-500 truncate">
                          {testimonial.role}
                          {testimonial.company && `, ${testimonial.company}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 ml-11 sm:ml-0">
                      <Badge variant={testimonial.status === 'published' ? 'success' : 'warning'} className="text-xs">
                        {testimonial.status === 'published' ? t.admin.common.published : t.admin.common.draft}
                      </Badge>
                      {testimonial.is_featured && <Badge variant="primary" className="text-xs">{t.admin.common.featured}</Badge>}
                    </div>
                  </div>

                  <div className="flex mb-2 sm:mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  <p className="text-neutral-600 dark:text-neutral-400 text-xs sm:text-sm line-clamp-3 mb-3 sm:mb-4">
                    "{testimonial.quote}"
                  </p>

                  <div className="flex items-center justify-end gap-1 sm:gap-2 pt-3 sm:pt-4 border-t border-neutral-200 dark:border-neutral-700 -mx-3 sm:mx-0 px-3 sm:px-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFeatured(testimonial)}
                      title={testimonial.is_featured ? t.admin.common.removeFeatured : t.admin.common.markFeatured}
                      className="p-2"
                    >
                      <Star className={`w-4 h-4 ${testimonial.is_featured ? 'text-yellow-400 fill-current' : ''}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTogglePublish(testimonial)}
                      title={testimonial.status === 'published' ? t.admin.common.unpublish : t.admin.common.publish}
                      className="p-2"
                    >
                      {testimonial.status === 'published' ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" className="p-2">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      onClick={() => {
                        setTestimonialToDelete(testimonial);
                        setDeleteModalOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <EmptyState
            title={t.admin.testimonials.noTestimonials}
            description={t.admin.testimonials.noTestimonialsDesc}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />}>
                {t.admin.testimonials.createFirst}
              </Button>
            }
          />
        )}
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.testimonials.deleteTestimonial}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.testimonials.deleteConfirm.replace('{name}', testimonialToDelete?.name || '')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteTestimonial.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
